import { BreakpointObserver } from '@angular/cdk/layout';
import { HttpClient } from '@angular/common/http';
import { Component, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import {
    AuthService,
    BlockAbstractComponent,
    PageService,
    ParserService,
    SSOService,
} from '@gallaghercp/core-frontend';
import { APP_CONFIG } from '@core-app/app.config';

@Component({
    selector: 'core-profile',
    templateUrl: './block-splash-landing-page.component.html',
    styleUrls: ['./block-splash-landing-page.scss'],
    standalone: false,
})
export class BlockSplashLandingComponent extends BlockAbstractComponent implements OnInit {
    protected override parser: ParserService;
    protected override pages: PageService;
    protected override auth: AuthService;
    protected breakpoint = inject(BreakpointObserver);
    protected http = inject(HttpClient);
    protected sso = inject(SSOService);
    protected router = inject(Router);

    backgroundImage!: string;
    isNoButtons: boolean = false;
    showStdLink: boolean = false;
    showStdLink2: boolean = false;
    showSsoLink: boolean = false;
    showSsoLink2: boolean = false;

    constructor() {
        const parser = inject(ParserService);
        const pages = inject(PageService);
        const auth = inject(AuthService);

        super(parser, auth, pages);

        this.parser = parser;
        this.pages = pages;
        this.auth = auth;
    }

    override ngOnInit() {
        super.ngOnInit();
        this.initializeBackgroundImage();
        this.initializeLinkVisibility();
        this.evaluateButtonState();

        this.subscriptions.push(
            this.auth.authChangeEmit.subscribe(() => {
                const redirectRoute = (APP_CONFIG as any).zoneRouting.find(
                    (zr: any) => zr.id === this.auth.getUser()?.zone,
                )?.route;
                if (redirectRoute && this.router.url !== redirectRoute) {
                    this.router.navigate([redirectRoute]);
                }
            }),
        );
    }

    getHeadingStyle() {
        const size: unknown = this.block?.data?.headingFontSize;
        if (typeof size === 'string' && size.trim().length > 0) {
            return { 'font-size': size } as any;
        }
        return {};
    }

    getTextStyle() {
        const styles: Record<string, string> = {};

        const size: unknown = this.block?.data?.introFontSize;
        if (typeof size === 'string' && size.trim().length > 0) {
            styles['font-size'] = size as string;
        }

        const mb: unknown = this.block?.data?.introBottomMarginRem;
        if (typeof mb === 'string' && mb.trim().length > 0) {
            styles['margin-bottom'] = mb as string; // expects rem values from CMS
        }

        return styles as any;
    }

    getSplashBackground() {
        if (this.block?.data?.backgroundChoice === 'Image' && this.backgroundImage) {
            return {
                'background-image': `url(${this.backgroundImage})`,
                'background-color': null,
                'background-size': 'cover',
            } as any;
        }
        if (this.block?.data?.backgroundChoice === 'Colour' && this.block?.data?.backgroundColour) {
            return {
                'background-image': 'none',
                'background-color': this.block.data.backgroundColour,
            };
        }
        return { 'background-image': 'none' };
    }

    // This needs testing when we have an SSO to use
    initiateSSO(sso: string) {
        this.subscriptions.push(this.sso.initiate(sso, 'default', this.block.data.relayStateUrl || null).subscribe());
    }

    private initializeBackgroundImage(): void {
        if (this.block.data.backgroundChoice === 'Image' && this.block.data.image_url) {
            this.backgroundImage = this.block.data.image_url;
        }
    }

    private initializeLinkVisibility(): void {
        this.showStdLink = this.shouldShowStandardLink();
        this.showStdLink2 = this.shouldShowStandardLink2();
        this.showSsoLink = this.shouldShowSsoLink();
        this.showSsoLink2 = this.shouldShowSsoLink2();
    }

    private shouldShowStandardLink(): boolean {
        return !!(this.block.data.linkText && this.block.data.link && !this.block.data.ssoLink);
    }

    private shouldShowStandardLink2(): boolean {
        return !!(this.block.data.linkText2 && this.block.data.link2 && !this.block.data.ssoLink2);
    }

    private shouldShowSsoLink(): boolean {
        return !!(this.block.data.linkText && this.block.data.ssoLink);
    }

    private shouldShowSsoLink2(): boolean {
        return !!(this.block.data.linkText2 && this.block.data.ssoLink2);
    }

    private evaluateButtonState(): void {
        this.isNoButtons = !this.showStdLink && !this.showStdLink2 && !this.showSsoLink && !this.showSsoLink2;
    }
}
